import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import Refresh from '@mui/icons-material/Refresh';
import version from 'staticFiles/version.txt';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { Link as MUILink } from '@mui/material';
import { Link } from 'react-router-dom';
import { clearApplicationCache, refreshSWAndReload } from './utils';

export default function Footer({ minimal = false, variant = 'default' }) {
  const { t } = useTranslation();
  const [appVersion, setappVersion] = React.useState('');
  const [swWaiting, setSwWaiting] = React.useState(undefined);

  React.useEffect(() => {
    fetch(version)
      .then((r) => r.text())
      .then((text) => {
        setappVersion(text);
      });

    // If we see that the service worker is waiting
    // then we can display a more visible button than the refresh at the bottom
    window.addEventListener('onSwUpdate', (event) => {
      setSwWaiting(event.detail.waiting);
    });
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((serviceWorkerRegistration) => {
        if (serviceWorkerRegistration.waiting) {
          setSwWaiting(serviceWorkerRegistration.waiting);
        }
      });
    }
    return () => window.removeEventListener('onSwUpdate', (event) => {
      setSwWaiting(event.detail.waiting);
    });
  }, []);

  if (variant === 'thin') {
    return (
      <Box
        component="footer"
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100vw',
          py: 1,
          borderTop: '1px solid',
          borderColor: 'divider',
          zIndex: 10,
        }}
      >
        <Typography variant="caption">
          {t('settings.app_version', { appVersion })}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      component="footer"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        mt: 5,
        mb: 3,
      }}
    >
      {minimal === false && (
        <Typography variant="caption">
          {t('labox-apps')}
          <MUILink
            component={Link}
            to="/contact"
            color="primary"
            sx={{ fontWeight: 'bold' }}
          >
            contact@labox-apps.com
          </MUILink>
        </Typography>
      )}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Typography variant="caption">
          {t('settings.app_version', { appVersion })}
        </Typography>

        <Button
          variant="text"
          size="small"
          onClick={refreshSWAndReload}
          endIcon={<Refresh />}
        >
          <Typography variant="caption">
            {t('reload')}
          </Typography>
        </Button>
      </Box>
      <Button
        variant="text"
        size="small"
        onClick={clearApplicationCache}
        endIcon={<Refresh />}
        sx={{ my: 1 }}
      >
        <Typography variant="caption">
          {t('clear_cache')}
        </Typography>
      </Button>
      <Snackbar
        open={swWaiting}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="info"
          action={(
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={() => setSwWaiting(undefined)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
            )}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Typography variant="span">
              {t('new_version')}
            </Typography>
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                try {
                  swWaiting.postMessage({ type: 'SKIP_WAITING' });

                  setTimeout(() => {
                    window.location.reload();
                  }, 1500);
                } catch (error) {
                  console.error('Error during service worker update:', error);
                  window.location.reload();
                }
              }}
              endIcon={<Refresh />}
            >
              {t('reload')}
            </Button>

          </Box>
        </Alert>
      </Snackbar>
    </Box>
  );
}
